import ScButton from "@/src/components/ui-kit/button";
import {
  downloadImportTemplate,
  importInviteUsersFromExcel,
  type ImportExcelResult,
} from "@/src/utils/import-excel-users";
import * as DocumentPicker from "expo-document-picker";
import {
  CloseCircle,
  DocumentDownload,
  DocumentText1,
  ImportCurve,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";
import { Snackbar } from "react-native-paper";
import { useTheme } from "@/src/styles/theme-provider";

type ImportUsersModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  onImport: (result: ImportExcelResult) => void;
}>;

const ImportUsersModal = ({
  visible,
  onClose,
  onImport,
}: ImportUsersModalProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] =
    useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const { t } = useTranslation();
  const { varStyle } = useTheme();

  const pickFile = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        ".xlsx",
        ".xls",
      ],
      copyToCacheDirectory: true,
    });

    if (result.canceled) return;

    setSelectedFile(result.assets[0]);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    try {
      const result = await importInviteUsersFromExcel(selectedFile);
      if (result.imported.length === 0) {
        throw new Error(t("ERROR_NO_VALID_USER"));
      }
      onImport(result);
      setSelectedFile(null);
      onClose();
    } catch (error) {
      // Show the real reason. The bare `catch {}` here meant a CSP-blocked read,
      // a corrupt workbook and a header mismatch all looked identical, which is
      // why "import not working" was undiagnosable.
      console.error("User import failed", error);
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : t("ERROR_IMPORT_FAILED"),
      );
    }
  };

  const handleDownloadTemplate = () => {
    downloadImportTemplate().catch((error) => {
      console.error("Template download failed", error);
      setErrorMessage(t("ERROR_IMPORT_FAILED"));
    });
  };

  return (
    <Modal transparent visible={visible} animationType="fade">
      <View style={varStyle} className="flex-1 items-center justify-center bg-black/40">
        <View className="bg-chalk rounded-2xl w-[90%] max-w-[520px] p-6 gap-6">
          {/* Header */}
          <View className="flex-row justify-between items-center">
            <Text className="text-lg font-semibold text-onyx">
              {t("LABEL_IMPORT_USERS")}
            </Text>
            <Pressable onPress={onClose} className="p-1.5 rounded-full hover:bg-ash-3">
              <CloseCircle size={22} className="text-gray-dark" />
            </Pressable>
          </View>

          {/* Body */}
          <View className="flex-row gap-6">
            {/* Left */}
            <View className="flex-1">
              {!selectedFile ? (
                <Pressable
                  onPress={pickFile}
                  className="border border-dashed border-ash-3
                             rounded-xl p-6 items-center justify-center gap-3 hover:bg-ash-3"
                >
                  <ImportCurve size={32} className="text-primary" />
                  <ScButton
                    text="LABEL_SELECT_EXCEL_SHEET"
                    mode="outlined"
                    onPress={pickFile}
                  ></ScButton>
                </Pressable>
              ) : (
                /* File Card */
                <View className="border border-ash-3 rounded-xl p-4 flex-row items-center gap-3">
                  <DocumentText1 size={28} className="text-primary" />
                  <View className="flex-1">
                    <Text className="text-sm font-medium text-onyx" numberOfLines={1}>
                      {selectedFile.name}
                    </Text>
                    <Text className="text-xs text-gray-dark">
                      {(selectedFile.size! / 1024).toFixed(1)} {t("LABEL_KB")}
                    </Text>
                  </View>

                  <Pressable onPress={() => setSelectedFile(null)} className="p-1.5 rounded-full hover:bg-ash-3">
                    <CloseCircle size={20} className="text-gray-dark" />
                  </Pressable>
                </View>
              )}
            </View>

            {/* Right */}
            <View className="flex-1 gap-2">
              <Text className="text-sm font-semibold text-onyx">
                {t("LABEL_FIELDS_FOR_UPLOAD")}
              </Text>
              <Text className="text-sm text-gray-dark">
                • {t("LABEL_EMAIL_ADDRESS_DESC")}
              </Text>
              <Text className="text-sm text-gray-dark">
                • {t("LABEL_FIRST_NAME_DESC")}
              </Text>
              <Text className="text-sm text-gray-dark">
                • {t("LABEL_LAST_NAME_DESC")}
              </Text>
              <Text className="text-sm text-gray-dark">
                • {t("LABEL_ROLE_DESC")}
              </Text>

              <Pressable
                onPress={handleDownloadTemplate}
                className="flex-row items-center gap-1.5 mt-2 self-start"
              >
                <DocumentDownload size={16} className="text-primary" />
                <Text className="text-sm text-primary font-medium">
                  {t("BTN_DOWNLOAD_TEMPLATE")}
                </Text>
              </Pressable>
            </View>
          </View>

          {/* Footer */}
          <View className="flex-row justify-end gap-3">
            <ScButton
              text="BTN_CANCEL"
              mode="outlined"
              onPress={onClose}
            ></ScButton>

            <ScButton
              text="BTN_INVITE"
              disabled={!selectedFile}
              onPress={handleImport}
            ></ScButton>
          </View>
        </View>
      </View>
      <Snackbar
        visible={!!errorMessage}
        onDismiss={() => setErrorMessage(null)}
        duration={3000}
      >
        {errorMessage}
      </Snackbar>
    </Modal>
  );
};

export default ImportUsersModal;
